// Manual pagination for the printed Salary Statement.
//
// Browsers don't expose "which physical page is this" to CSS/JS, so a table
// that's simply allowed to flow across pages natively has no reliable way to
// print a correctly-incrementing "Page N" on each physical page it spans.
// Instead, we estimate how many rows fit on one printed page ourselves and
// explicitly chunk each group's rows into table-blocks - each group's table
// (or, for a big group, each split chunk of it) is one block. Blocks are
// then packed onto physical pages: as many consecutive blocks as actually
// fit share one physical page (each still prints its own heading), and a
// forced page break only goes in where the next block genuinely doesn't fit.
//
// This is inherently an estimate (converting CSS px to real printed
// millimeters at a standard 96dpi assumption) - if it doesn't exactly match
// a given browser/printer after real testing, override it directly via
// reportLayoutConfig.js's `rowsPerPage` field instead of touching this file.

const A4_LANDSCAPE_HEIGHT_MM = 210
const MM_PER_CM = 10
const PX_PER_MM = 96 / 25.4 // standard CSS px <-> physical size mapping

// Prepared By / Verified By / Authorised Signature - one real table row (a
// single merged cell holding all three, side by side) extending every
// group's table right after its TOTAL row (see SalaryStatement.js), not a
// separate element placed after the table. Roughly 3 normal row-heights
// tall (~2 blank lines + 1 label line).
export const FOOTER_LABELS = ['Prepared By', 'Verified By', 'Authorised Signature']
const FOOTER_ROW_HEIGHT_MULTIPLE = 3

// The Employee column is narrow enough (relative to how many columns share
// the table's width - see SalaryStatement.js) that a long name wraps onto a
// second line, making that row roughly twice as tall as a normal one.
// Row-count-based pagination has no way to measure real rendered height (see
// file header), so a long name is weighted as 2 rows instead of 1 to
// approximate the extra space it actually takes. Department names never get
// long enough to wrap, so only Employee is checked. This is a heuristic on
// character count, not measured pixel width, so it won't be exact for every
// name - tuned empirically against real printed output; adjust the
// threshold if it drifts.
const LONG_NAME_CHAR_THRESHOLD = 17

function rowWeight(row) {
  return (row.employee || '').length > LONG_NAME_CHAR_THRESHOLD ? 2 : 1
}

function px(cssSizeString) {
  return parseFloat(cssSizeString)
}

// Fixed overhead a single group's table-block costs on its own (heading +
// subheading + sheet padding/border + the table's own header row), in
// row-height units. This repeats once per block even when several blocks
// share one physical page, since each keeps printing its own heading.
function blockOverheadRows(config) {
  const rowHeightPx = px(config.rowHeight)
  const sheetPadding = 16 * 2 // .statement-sheet padding (border removed at print)
  const headingHeight = px(config.headingFontSize) * 1.3 + 7.2 + 7.2 + 1.8 // line-height + margin/padding/border-bottom
  const subheadingHeight = px(config.subheadingFontSize) * 1.3 + 14.4 + 9 + 1.8
  const tableHeaderRowHeight = rowHeightPx
  return (sheetPadding + headingHeight + subheadingHeight + tableHeaderRowHeight) / rowHeightPx
}

// The "Page N" label's overhead, in row-height units - kept as a small
// fixed budget reservation even though the label itself (SalaryStatement.js)
// is position:absolute and adds no real document-flow height, and is shown
// on every block sharing a physical page (not just the first), so a group
// that lands second or later on a shared page still displays its own page
// number rather than relying on a preceding block's copy.
function pageNumberOverheadRows(config) {
  const rowHeightPx = px(config.rowHeight)
  return (10 * 1.3 + 12) / rowHeightPx
}

// How many data rows fit in one group's table-block, already reserving room
// for that block's own TOTAL row and signature row (see the reserve below).
function estimateRowsPerPage(config) {
  let pageBudgetRows

  if (config.rowsPerPage) {
    // reportLayoutConfig.js's `rowsPerPage` is the same budget the CSS
    // estimate below produces - the TOTAL row and the signature row both
    // count against it, not on top of it, so the reservation below still
    // applies to an explicit override the same way it does to the estimate.
    pageBudgetRows = config.rowsPerPage
  } else {
    const rowHeightPx = px(config.rowHeight)
    const pageHeightMm = A4_LANDSCAPE_HEIGHT_MM - 2 * config.pageMarginCm * MM_PER_CM
    const pageHeightPx = pageHeightMm * PX_PER_MM
    pageBudgetRows = pageHeightPx / rowHeightPx - blockOverheadRows(config) - pageNumberOverheadRows(config)
  }

  // Reserved on every page (not just the actual last one) so we don't need
  // to know in advance whether a given chunk will turn out to be the last -
  // costs a little unused whitespace on non-last pages, in exchange for
  // avoiding a chicken-and-egg sizing problem.
  const totalRowReserve = 1
  const footerReserve = FOOTER_ROW_HEIGHT_MULTIPLE
  return Math.max(1, Math.floor(pageBudgetRows) - totalRowReserve - footerReserve)
}

// The raw row-unit budget of one physical page, undiminished by any single
// block's own heading - derived from estimateRowsPerPage's result by adding
// back exactly what that function reserved (one block's heading overhead +
// TOTAL + signature row), so it stays consistent whether that number came
// from the CSS estimate or a manual override.
function estimatePhysicalPageBudget(config) {
  const rowsPerPage = estimateRowsPerPage(config)
  const reserve = 1 + FOOTER_ROW_HEIGHT_MULTIPLE
  return rowsPerPage + blockOverheadRows(config) + reserve
}

// Packs rows into table-block-sized chunks by weight (see rowWeight above)
// instead of flat count, so a block with several long, wrapped names holds
// fewer rows than a block of short names - both end up roughly the same
// actual printed height.
function packRowsByWeight(rows, capacity) {
  const chunks = []
  let current = []
  let currentWeight = 0

  rows.forEach((row) => {
    const weight = rowWeight(row)
    if (current.length > 0 && currentWeight + weight > capacity) {
      chunks.push(current)
      current = []
      currentWeight = 0
    }
    current.push(row)
    currentWeight += weight
  })
  if (current.length > 0) chunks.push(current)

  return chunks
}

// How much physical-page space a single table-block actually consumes: its
// own heading overhead plus its real (not maximum) row weight, TOTAL row,
// and signature row.
function blockConsumedRows(block) {
  const dataWeight = block.rows.reduce((sum, row) => sum + rowWeight(row), 0)
  return dataWeight + 1 + FOOTER_ROW_HEIGHT_MULTIPLE
}

// Packs table-blocks onto physical pages: as many consecutive blocks as fit
// within one physical page's budget share it (order is preserved - a group
// never gets reordered relative to another), and a page break only goes in
// before whichever block didn't fit.
function packBlocksIntoPhysicalPages(blocks, config) {
  const budget = estimatePhysicalPageBudget(config)
  const overheadPerBlock = blockOverheadRows(config)
  const physicalPages = []
  let current = []
  let currentWeight = 0

  blocks.forEach((block) => {
    const weight = overheadPerBlock + blockConsumedRows(block)
    if (current.length > 0 && currentWeight + weight > budget) {
      physicalPages.push(current)
      current = []
      currentWeight = 0
    }
    current.push(block)
    currentWeight += weight
  })
  if (current.length > 0) physicalPages.push(current)

  return physicalPages
}

// Turns [{ name, rows }] group sections into a flat, document-ordered list of
// physical pages - each entry is the array of table-block(s) that share that
// physical page. The caller's array index over the result IS the global
// page number.
export function paginateSections(sections, config) {
  const rowsPerPage = estimateRowsPerPage(config)
  const blocks = []

  sections.forEach((section) => {
    const numberedRows = section.rows.map((row, index) => ({ ...row, slNo: index + 1 }))
    const chunks = packRowsByWeight(numberedRows, rowsPerPage)

    chunks.forEach((chunkRows, chunkIndex) => {
      blocks.push({
        groupName: section.name,
        rows: chunkRows,
        groupRows: numberedRows,
        isLastPageOfGroup: chunkIndex === chunks.length - 1,
      })
    })
  })

  return packBlocksIntoPhysicalPages(blocks, config)
}
